import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { BarFill, BarTrack } from 'pages/HrDashboard/hrLayout.styled'
import { tokens } from 'styling/tokens'
import { IMaterialDelivery } from 'types/materialReceipt.interface'
import { computeYield, deriveState, discrepancyOf, fmt, SUPPLIERS, taskForStation } from './materialReceiptData'
import { MaterialReceiptPage } from './MaterialReceiptShell'
import { BreakdownRow, Grid2, KpiGrid, ListLine } from './MaterialReceipt.styled'

export const MaterialDashboard = () => {
  const navigate = useNavigate()
  const { deliveries, config } = useSelector((s: RootState) => s.materialReceipt)

  const todayISO = new Date().toISOString().slice(0, 10)
  const goldOf = (d: IMaterialDelivery) => computeYield(d, config.basis)?.gold ?? 0
  const netOf = (d: IMaterialDelivery) => computeYield(d, config.basis)?.netWet ?? 0

  const todays = deliveries.filter((d) => d.date === todayISO)
  const goldToday = todays.reduce((s, d) => s + goldOf(d), 0)
  const tonsToday = todays.reduce((s, d) => s + netOf(d), 0)
  const open = deliveries.filter((d) => ['ARRIVED', 'IN_PROGRESS', 'COMPLETED'].includes(deriveState(d)))
  const flagged = deliveries.filter((d) => discrepancyOf(d, config.threshold)?.breach && !d.caseResolved)
  const finalizeReady = deliveries.filter((d) => taskForStation(d, 'MANAGER')?.kind === 'finalize')

  const bySupplier = SUPPLIERS.map((name) => ({
    name,
    gold: deliveries.filter((d) => d.supplier === name).reduce((s, d) => s + goldOf(d), 0),
  }))
    .filter((s) => s.gold > 0)
    .sort((a, b) => b.gold - a.gold)
  const maxGold = Math.max(1, ...bySupplier.map((s) => s.gold))

  const waiting = [
    ...finalizeReady.map((d) => ({ d, reason: 'Ready to finalize', tone: 'success' as const })),
    ...flagged.map((d) => ({ d, reason: 'Grade discrepancy', tone: 'danger' as const })),
  ]

  return (
    <MaterialReceiptPage requireManager title='Dashboard' subtitle={`Grade basis: ${config.basis} · tolerance ±${config.threshold}%`}>
      {() => (
        <>
          <KpiGrid>
            <MetricCard label='Deliveries today' value={String(todays.length)} />
            <MetricCard label='Expected gold (today)' value={`${fmt(goldToday)} g`} accentColor={tokens.accent.base} />
            <MetricCard label='Tonnage received (today)' value={`${fmt(tonsToday, 1)} t`} />
            <MetricCard label='Open tickets' value={String(open.length)} trailing={<StatusPill status='info'>in flight</StatusPill>} />
            <MetricCard
              label='Flagged cases'
              value={String(flagged.length)}
              trailing={flagged.length ? <StatusPill status='danger'>review</StatusPill> : <StatusPill status='success'>clear</StatusPill>}
            />
          </KpiGrid>

          <Grid2>
            <Container>
              <ContainerHeader><TitleText weight='bold'>Throughput by supplier</TitleText></ContainerHeader>
              <ContainerContent>
                {bySupplier.length ? (
                  bySupplier.map((s) => (
                    <BreakdownRow key={s.name}>
                      <BodyText style={{ margin: 0 }}>{s.name}</BodyText>
                      <BodyText weight='bold' style={{ margin: 0 }}>{fmt(s.gold)} g</BodyText>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <BarTrack>
                          <BarFill $pct={(s.gold / maxGold) * 100} />
                        </BarTrack>
                      </div>
                    </BreakdownRow>
                  ))
                ) : (
                  <BodyText grey>No completed yield yet.</BodyText>
                )}
              </ContainerContent>
            </Container>

            <Container>
              <ContainerHeader>
                <Flex between center style={{ width: '100%' }}>
                  <TitleText weight='bold'>Waiting on you</TitleText>
                  {waiting.length > 0 && <StatusPill status='warning'>{waiting.length}</StatusPill>}
                </Flex>
              </ContainerHeader>
              <ContainerContent>
                {waiting.length ? (
                  waiting.map(({ d, reason, tone }) => (
                    <ListLine key={`${d.id}-${reason}`} onClick={() => navigate(`/material-receipt/ticket/${d.id}`)}>
                      <div>
                        <BodyText weight='600' style={{ margin: 0 }}>{d.id}</BodyText>
                        <OtherText grey>{d.supplier} · {d.plate}</OtherText>
                      </div>
                      <StatusPill status={tone}>{reason}</StatusPill>
                    </ListLine>
                  ))
                ) : (
                  <Flex column center gap='0.25rem' style={{ padding: '1rem 0', textAlign: 'center' }}>
                    <div style={{ fontSize: '1.4rem' }}>✓</div>
                    <BodyText style={{ margin: 0 }}>All clear — nothing awaiting your sign-off.</BodyText>
                  </Flex>
                )}
              </ContainerContent>
            </Container>
          </Grid2>
        </>
      )}
    </MaterialReceiptPage>
  )
}
